import { DocsSpaceAccessDialog } from "./DocsSpaceAccessDialog";
import { docVerificationState } from "../../shared/operations-docs-maintenance";
import type { OpsDocEmployeePrincipal, OpsDocGroup } from "../../shared/operations-docs-schemas";
import { ChevronRight, CircleAlert, FileText, Folder, LockKeyhole, Plus } from "lucide-react";
import { useState } from "react";

import type { OpsDocPageSummary, OpsDocSpace } from "../../shared/operations-docs-schemas";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Field, FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import type { OperationsNavigate } from "./navigation";
import { useOperationsPrefetch } from "./operations-prefetch-context";

type CreatePage = (
  spaceId: string,
  parentPageId: string | null,
  title: string,
) => Promise<void> | void;

function useCreateDialog(onCreate: (value: string) => Promise<void> | void) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const close = () => {
    setOpen(false);
    setValue("");
  };
  const create = async () => {
    const nextValue = value.trim();
    if (!nextValue) return;
    try {
      await onCreate(nextValue);
      close();
    } catch {
      // The mutation error is rendered by the caller and the draft remains available to retry.
    }
  };
  return {
    create,
    onOpenChange: (nextOpen: boolean) => {
      if (nextOpen) setOpen(true);
      else close();
    },
    open,
    setValue,
    value,
  };
}

function CreatePageDialog({
  error,
  isCreating,
  onCreate,
  parentPageId,
  spaceName,
}: {
  error: Error | null;
  isCreating: boolean;
  onCreate: (title: string) => Promise<void> | void;
  parentPageId: string | null;
  spaceName: string;
}) {
  const dialog = useCreateDialog(onCreate);
  return (
    <Dialog onOpenChange={dialog.onOpenChange} open={dialog.open}>
      <DialogTrigger
        aria-label={parentPageId ? "Add child page" : `Add page to ${spaceName}`}
        render={<Button size="icon" variant="ghost" />}
      >
        <Plus />
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New page</DialogTitle>
          <DialogDescription>
            {parentPageId
              ? "The new page starts with its parent's access."
              : `Create a root page in ${spaceName}.`}
          </DialogDescription>
        </DialogHeader>
        <DialogForm
          aria-label="New page"
          onSubmit={(event) => {
            event.preventDefault();
            void dialog.create();
          }}
        >
          <DialogBody>
            <Field>
              <FieldLabel htmlFor={`new-page-${parentPageId ?? spaceName}`}>Page title</FieldLabel>
              <Input
                autoFocus
                id={`new-page-${parentPageId ?? spaceName}`}
                maxLength={200}
                onChange={(event) => {
                  dialog.setValue(event.target.value);
                }}
                required
                value={dialog.value}
              />
            </Field>
            {error ? <FieldError>{error.message}</FieldError> : null}
          </DialogBody>
          <DialogFooter>
            <Button disabled={isCreating || dialog.value.trim().length === 0} type="submit">
              {isCreating ? "Creating…" : "Create page"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

type TreePageProps = {
  activePageId: string | null;
  depth: number;
  navigate: OperationsNavigate;
  onCreatePage: CreatePage;
  page: OpsDocPageSummary;
  pages: OpsDocPageSummary[];
  pageCreateError: Error | null;
  pageCreating: boolean;
  space: OpsDocSpace;
};

function PageReviewIndicator({ page }: { page: OpsDocPageSummary }) {
  return docVerificationState(page) === "Needs review" ? (
    <span title="Needs review" aria-label="Needs review">
      <CircleAlert className="size-3 shrink-0 text-warning" />
    </span>
  ) : null;
}
function SpaceIcon({ space }: { space: OpsDocSpace }) {
  const Icon = space.accessMode === "restricted" ? LockKeyhole : Folder;
  return <Icon className="size-4 text-muted-foreground" />;
}
function PageTreeLabel({ page }: { page: OpsDocPageSummary }) {
  return (
    <>
      <FileText className="size-3.5 shrink-0 text-muted-foreground" />
      <span className="truncate">{page.title}</span>
      <PageReviewIndicator page={page} />{" "}
    </>
  );
}
function PageOpenButton({
  page,
  activePageId,
  navigate,
}: Pick<TreePageProps, "page" | "activePageId" | "navigate">) {
  const prefetch = useOperationsPrefetch();
  return (
    <button
      aria-current={activePageId === page.id ? "page" : undefined}
      onFocus={() => {
        prefetch?.(`/docs/${page.id}`);
      }}
      onPointerEnter={(event) => {
        if (event.pointerType !== "touch") prefetch?.(`/docs/${page.id}`);
      }}
      className="flex min-h-10 min-w-0 flex-1 items-center gap-2 px-2 py-2 text-left text-sm font-medium focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]"
      onClick={() => {
        navigate(`/docs/${page.id}`);
      }}
      type="button"
    >
      <PageTreeLabel page={page} />
    </button>
  );
}

function TreePage({
  activePageId,
  depth,
  navigate,
  onCreatePage,
  page,
  pages,
  pageCreateError,
  pageCreating,
  space,
}: TreePageProps) {
  const [expanded, setExpanded] = useState(true);
  const children = pages.filter((candidate) => candidate.parentPageId === page.id);
  const canCreate = page.accessLevel === "edit" || page.accessLevel === "manage";
  return (
    <li>
      <div
        className={`group/page flex min-h-9 items-center rounded-sm ${
          activePageId === page.id ? "bg-info-background text-link" : "hover:bg-muted"
        }`}
        style={{ paddingInlineStart: `${Math.min(depth, 8) * 14 + 4}px` }}
      >
        {children.length > 0 ? (
          <button
            aria-label={`${expanded ? "Collapse" : "Expand"} ${page.title}`}
            aria-expanded={expanded}
            className="flex size-8 shrink-0 items-center justify-center rounded-sm hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring"
            onClick={() => {
              setExpanded(!expanded);
            }}
          >
            <ChevronRight className={`size-3.5 ${expanded ? "rotate-90" : ""}`} />
          </button>
        ) : (
          <span className="w-2 shrink-0" />
        )}
        <PageOpenButton page={page} activePageId={activePageId} navigate={navigate} />
        {canCreate ? (
          <span className="mr-1 opacity-100 sm:opacity-0 sm:group-hover/page:opacity-100 sm:group-focus-within/page:opacity-100">
            <CreatePageDialog
              error={pageCreateError}
              isCreating={pageCreating}
              onCreate={(title) => onCreatePage(space.id, page.id, title)}
              parentPageId={page.id}
              spaceName={space.name}
            />
          </span>
        ) : null}
      </div>
      {children.length > 0 && expanded ? (
        <ul>
          {children.map((child) => (
            <TreePage
              activePageId={activePageId}
              depth={depth + 1}
              key={child.id}
              navigate={navigate}
              onCreatePage={onCreatePage}
              page={child}
              pageCreateError={pageCreateError}
              pageCreating={pageCreating}
              pages={pages}
              space={space}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function CreateSpaceDialog({
  error,
  isCreating,
  onCreate,
}: {
  error: Error | null;
  isCreating: boolean;
  onCreate: (name: string) => Promise<void> | void;
}) {
  const dialog = useCreateDialog(onCreate);
  return (
    <Dialog onOpenChange={dialog.onOpenChange} open={dialog.open}>
      <DialogTrigger render={<Button className="w-full justify-start" variant="ghost" />}>
        <Plus /> New space
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New documentation space</DialogTitle>
          <DialogDescription>
            New spaces are restricted to you. Add employees or groups in Space access.
          </DialogDescription>
        </DialogHeader>
        <DialogForm
          aria-label="New documentation space"
          onSubmit={(event) => {
            event.preventDefault();
            void dialog.create();
          }}
        >
          <DialogBody>
            <Field>
              <FieldLabel htmlFor="new-docs-space">Space name</FieldLabel>
              <Input
                id="new-docs-space"
                maxLength={120}
                onChange={(event) => {
                  dialog.setValue(event.target.value);
                }}
                required
                value={dialog.value}
              />
            </Field>
            {error ? <FieldError>{error.message}</FieldError> : null}
          </DialogBody>
          <DialogFooter>
            <Button disabled={isCreating || dialog.value.trim().length === 0} type="submit">
              {isCreating ? "Creating…" : "Create space"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

type DocsTreeProps = {
  activePageId: string | null;
  canManage: boolean;
  canWrite: boolean;
  employees: OpsDocEmployeePrincipal[];
  groups: OpsDocGroup[];
  navigate: OperationsNavigate;
  onCreatePage: CreatePage;
  onCreateSpace: (name: string) => Promise<void> | void;
  pageCreateError: Error | null;
  pageCreating: boolean;
  pages: OpsDocPageSummary[];
  spaceCreateError: Error | null;
  spaceCreating: boolean;
  spaces: OpsDocSpace[];
};

function SpaceHeading({ space }: { space: DocsTreeProps["spaces"][number] }) {
  return (
    <h2
      className="min-w-0 flex-1 truncate text-sm font-bold"
      title={space.name}
      id={`space-${space.id}`}
    >
      {space.name}
    </h2>
  );
}
function SpacePages({
  activePageId,
  canWrite,
  navigate,
  onCreatePage,
  pageCreateError,
  pageCreating,
  pages,
  spaces,
  employees,
  groups,
}: Pick<
  DocsTreeProps,
  | "activePageId"
  | "canWrite"
  | "navigate"
  | "onCreatePage"
  | "pageCreateError"
  | "pageCreating"
  | "pages"
  | "spaces"
  | "employees"
  | "groups"
>) {
  if (spaces.length === 0) {
    return <p className="px-2 py-4 text-sm text-muted-foreground">No documentation spaces yet.</p>;
  }
  return (
    <div className="space-y-4">
      {spaces.map((space) => {
        const spacePages = pages.filter((page) => page.spaceId === space.id);
        const rootPages = spacePages.filter((page) => page.parentPageId === null);
        return (
          <section key={space.id} aria-labelledby={`space-${space.id}`}>
            <div className="group/space flex min-h-9 items-center gap-1 rounded-sm px-1">
              <SpaceIcon space={space} />
              <SpaceHeading space={space} />
              {canWrite && space.accessLevel === "manage" && (
                <DocsSpaceAccessDialog space={space} employees={employees} groups={groups} />
              )}
              {canWrite && space.accessLevel !== "view" ? (
                <CreatePageDialog
                  error={pageCreateError}
                  isCreating={pageCreating}
                  onCreate={(title) => onCreatePage(space.id, null, title)}
                  parentPageId={null}
                  spaceName={space.name}
                />
              ) : null}
            </div>
            {rootPages.length === 0 ? (
              <p className="px-8 py-2 text-xs text-muted-foreground">No accessible pages.</p>
            ) : (
              <ul>
                {rootPages.map((page) => (
                  <TreePage
                    activePageId={activePageId}
                    depth={0}
                    key={page.id}
                    navigate={navigate}
                    onCreatePage={onCreatePage}
                    page={page}
                    pageCreateError={pageCreateError}
                    pageCreating={pageCreating}
                    pages={spacePages}
                    space={space}
                  />
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

export function DocsTree(props: DocsTreeProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="flex min-h-12 items-center justify-between border-b px-4 text-sm font-semibold lg:hidden"
        aria-expanded={mobileOpen}
        aria-controls="docs-page-tree"
        onClick={() => {
          setMobileOpen(!mobileOpen);
        }}
      >
        Browse pages <ChevronRight className={`size-4 ${mobileOpen ? "rotate-90" : ""}`} />
      </button>
      <aside
        id="docs-page-tree"
        className={`${mobileOpen ? "block" : "hidden"} max-h-72 overflow-y-auto border-b bg-sidebar/40 p-3 lg:sticky lg:top-0 lg:max-h-[calc(100dvh-3rem)] lg:min-h-[calc(100dvh-3rem)] lg:border-r lg:border-b-0 lg:block`}
        aria-label="Documentation pages"
      >
        <div className="mb-2 px-2 py-1 text-xs font-semibold text-muted-foreground">Spaces</div>
        <SpacePages {...props} />
        {props.canManage ? (
          <div className="mt-4 border-t pt-3">
            <CreateSpaceDialog
              error={props.spaceCreateError}
              isCreating={props.spaceCreating}
              onCreate={props.onCreateSpace}
            />
          </div>
        ) : null}
      </aside>
    </>
  );
}
